import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface ContractPricingItem {
  contract_item_id: string;
  item_id: string;
  item_code: string;
  item_name: string;
  unit_price: number;
  discount_percentage: number;
  final_price: number;
  min_quantity: number;
  max_quantity?: number;
  is_active: boolean;
}

export interface ContractPricing {
  contract_id: string;
  vendor_id: string;
  vendor_name: string;
  contract_number: string;
  contract_name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  payment_terms?: string;
  min_order_value: number;
  discount_percentage: number;
  document_url?: string;
  notes?: string;
  items: ContractPricingItem[];
  created_at: string;
}

export interface ContractListResponse {
  contracts: ContractPricing[];
  total: number;
  page: number;
  page_size: number;
}

export interface PriceLookupItem {
  item_id: string;
  vendor_id?: string;
  quantity: number;
}

export interface PriceLookupResult {
  item_id: string;
  item_code: string;
  item_name: string;
  best_price?: number;
  best_contract_id?: string;
  best_contract_name?: string;
  vendor_id?: string;
  vendor_name?: string;
  has_contract: boolean;
}

export interface ExpiringContract {
  contract_id: string;
  vendor_id: string;
  vendor_name: string;
  contract_number: string;
  contract_name: string;
  end_date: string;
  days_remaining: number;
}

export function useContracts() {
  const queryClient = useQueryClient();

  const getContracts = (params?: {
    vendor_id?: string;
    is_active?: boolean;
    search?: string;
    page?: number;
    page_size?: number;
  }) =>
    useQuery<ContractListResponse>({
      queryKey: ['contracts', params],
      queryFn: () => api.get('/contracts', { params }).then((res) => res.data),
    });

  const getContract = (contractId: string) =>
    useQuery<ContractPricing>({
      queryKey: ['contract', contractId],
      queryFn: () => api.get(`/contracts/${contractId}`).then((res) => res.data),
      enabled: !!contractId,
    });

  const getExpiringContracts = (days: number = 30) =>
    useQuery<ExpiringContract[]>({
      queryKey: ['expiring-contracts', days],
      queryFn: () => api.get('/contracts/expiring', { params: { days } }).then((res) => res.data),
    });

  const createContract = () =>
    useMutation({
      mutationFn: (data: {
        vendor_id: string;
        contract_number: string;
        contract_name: string;
        start_date: string;
        end_date: string;
        payment_terms?: string;
        min_order_value?: number;
        discount_percentage?: number;
        document_url?: string;
        notes?: string;
        items: Array<{
          item_id: string;
          item_code: string;
          item_name: string;
          unit_price: number;
          discount_percentage?: number;
          min_quantity?: number;
          max_quantity?: number;
        }>;
      }) => api.post('/contracts', data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['contracts'] });
      },
    });

  const updateContract = () =>
    useMutation({
      mutationFn: ({
        contractId,
        data,
      }: {
        contractId: string;
        data: Partial<{
          contract_name: string;
          start_date: string;
          end_date: string;
          payment_terms: string;
          min_order_value: number;
          discount_percentage: number;
          document_url: string;
          notes: string;
          is_active: boolean;
        }>;
      }) => api.post(`/contracts/${contractId}`, data),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['contracts'] });
        queryClient.invalidateQueries({ queryKey: ['contract', variables.contractId] });
      },
    });

  const deleteContract = () =>
    useMutation({
      mutationFn: (contractId: string) => api.post(`/contracts/${contractId}`, { is_active: false }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['contracts'] });
      },
    });

  const lookupPrice = () =>
    useMutation({
      mutationFn: (items: PriceLookupItem[]) => api.post('/contracts/price-lookup', items),
    });

  return {
    getContracts,
    getContract,
    getExpiringContracts,
    createContract,
    updateContract,
    deleteContract,
    lookupPrice,
  };
}
